import { useEffect, useRef, useState } from 'react'
import { useUploadLogo, type ClinicLogo, type LogoPosition } from './api'
import {
  KEEP_LOGO,
  inspectLogoFile,
  logoFileName,
  logoPreview,
  logoUploadProblem,
  type LogoFormState,
} from './logo'
import type { LogoBlockProps } from './logo-block'
import type { FormController } from './use-form-state'

/**
 * O logo dentro do formulário de Empresa (RN-ADM-05). Ao escolher o arquivo, a tela o confere, pede
 * a reserva à API e o envia direto ao Storage; a troca fica guardada no formulário e só vale quando
 * a empresa é salva com o `logoId`. Remover também espera o salvar. A prévia do que foi enviado é
 * local (o arquivo escolhido), porque o logo ainda não é o da clínica.
 */
export function useLogoField<T extends LogoFormState>(
  form: FormController<T>,
  saved: ClinicLogo | null,
) {
  const upload = useUploadLogo()
  // O que não deixou o arquivo passar, na conferência ou no envio.
  const [problem, setProblem] = useState<string | null>(null)
  const previewUrl = useRef<string | null>(null)
  const { logo, logoPosition } = form.values

  function releasePreview() {
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current)
    previewUrl.current = null
  }
  // A prévia local é um endereço do navegador: ao sair da tela, ele é devolvido.
  useEffect(() => releasePreview, [])

  function change(next: LogoFormState['logo']) {
    form.update({ logo: next } as Partial<T>)
  }

  async function choose(file: File) {
    setProblem(null)
    const inspection = await inspectLogoFile(file)
    if (!inspection.ok) {
      setProblem(inspection.message)
      return
    }
    const { mimeType, width, height } = inspection.image
    upload.mutate(
      { file, fileName: logoFileName(file.name), mimeType },
      {
        onSuccess: (ticket) => {
          releasePreview()
          previewUrl.current = URL.createObjectURL(file)
          change({
            action: 'replace',
            logoId: ticket.logoId,
            previewUrl: previewUrl.current,
            width,
            height,
          })
        },
        onError: (error) => setProblem(logoUploadProblem(error)),
      },
    )
  }

  function remove() {
    setProblem(null)
    releasePreview()
    change({ action: 'remove' })
  }

  function undo() {
    setProblem(null)
    releasePreview()
    change(KEEP_LOGO)
  }

  const block: Omit<LogoBlockProps, 'readOnly'> = {
    preview: logoPreview(logo, saved),
    change: logo.action,
    position: logoPosition,
    onPositionChange: (position: LogoPosition) =>
      form.update({ logoPosition: position } as Partial<T>),
    onChoose: (file) => void choose(file),
    onRemove: remove,
    onUndo: undo,
    uploading: upload.isPending,
    // O que a API recusa ao salvar (422 em `logo` ou `logoId`) aparece no mesmo lugar.
    error: problem ?? form.errors.logo ?? form.errors.logoPosition ?? null,
  }

  return {
    /** As props do `LogoBlock`. */
    block,
    isUploading: upload.isPending,
    /** Depois de salvar: o que foi enviado virou o logo da clínica, e não há mais troca pendente. */
    settle: undo,
  }
}
